import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';
import { CaseConferencingNoteForm } from './CaseConferencingNoteForm';

const PARTICIPANT_DETAIL = gql`
  query CaseConferencingParticipantDetail($participant: Int!) {
    caseConferencingParticipantDetail(participant: $participant) {
      id
      from_date
      to_date
      medications { medicine_name frequency strength times_per_day }
      adherence { adherence_rate mparticipant_id total_expected_doses total_taken_doses total_days message }
    }
  }
`;

const PARTICIPANT_NOTES = gql`
  query CaseConferencingParticipantNotes($participant: Int!) {
    caseConferencingParticipantNotes(participant: $participant) { note updated_at }
  }
`;

interface ParticipantDetail {
  from_date: string | null;
  to_date: string | null;
  medications: Array<{ medicine_name: string | null; frequency: string | null; strength: string | null; times_per_day: string | null }>;
  adherence: {
    adherence_rate: number | null;
    mparticipant_id: number | null;
    total_expected_doses: number | null;
    total_taken_doses: number | null;
    total_days: number | null;
    message: string | null;
  } | null;
}

interface ParticipantNote { note: string | null; updated_at: string | null }

interface DetailsProps {
  participant: number;
  odrparticipant: number | null;
  name: string;
  onClose: () => void;
}

export const CaseConferencingDetailsDialog = ({ participant, odrparticipant, name, onClose }: DetailsProps) => {
  const { user } = useUser();
  const [noteFormOpen, setNoteFormOpen] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const canReadNotes = hasAnyPolicy(user, ['program:participant:coordinatedcare~full']);
  const canOpenProfile = hasAnyPolicy(user, ['program:participant~read']);
  const canOpenMedCall = hasAnyPolicy(user, ['tools:medcall:access:~user']);
  const canAddNote = hasAnyPolicy(user, ['program:participant:coordinatedcare~full', 'tools:billing:access~full']);
  const detailQuery = useQuery<{ caseConferencingParticipantDetail: ParticipantDetail | null }>(PARTICIPANT_DETAIL, {
    variables: { participant }, fetchPolicy: 'network-only',
  });
  const notesQuery = useQuery<{ caseConferencingParticipantNotes: ParticipantNote[] | null }>(PARTICIPANT_NOTES, {
    variables: { participant }, fetchPolicy: 'network-only', skip: !canReadNotes,
  });
  const detail = detailQuery.data?.caseConferencingParticipantDetail;
  const notes = notesQuery.data?.caseConferencingParticipantNotes;
  const adherence = detail?.adherence;
  const detailUnavailable = Boolean(detailQuery.error || (!detailQuery.loading && detail === undefined));
  const notesUnavailable = Boolean(notesQuery.error || (!notesQuery.loading && notes == null));

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open && !noteSaving) onClose(); }}>
      <DialogContent className="overflow-y-auto" hideCloseButton={noteSaving}>
        <DialogHeader>
          <DialogTitle>Case Conferencing Details</DialogTitle>
          <DialogDescription>{name}</DialogDescription>
        </DialogHeader>

        {detailQuery.loading && <p role="status">Loading medication and adherence details...</p>}
        {detailUnavailable && (
          <div role="alert">
            <p>Unable to load medication and adherence details.</p>
            <Button onClick={() => { void detailQuery.refetch().catch(() => undefined); }}>Retry Details</Button>
          </div>
        )}
        {!detailQuery.loading && !detailUnavailable && !detail && <p>No MedCall record is linked to this participant.</p>}
        {!detailQuery.loading && !detailUnavailable && detail && (
          <>
            <section aria-label="Medications">
              <h2 className="font-semibold">Medications</h2>
              {detail.medications.length === 0 && <p>No active medications recorded.</p>}
              {detail.medications.length > 0 && (
                <table className="w-full text-left text-sm">
                  <thead><tr><th>Medication</th><th>Frequency</th><th>Strength</th></tr></thead>
                  <tbody>{detail.medications.map((medication, index) => (
                    <tr key={index}><td>{medication.medicine_name || '-'}</td><td>{medication.frequency || '-'}</td><td>{medication.strength || '-'}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </section>
            <section aria-label="Adherence">
              <h2 className="font-semibold">Medication Adherence</h2>
              <p>From {detail.from_date || '-'} to {detail.to_date || '-'}</p>
              <p>Average adherence: {adherence?.adherence_rate == null ? '-' : `${adherence.adherence_rate}%`}</p>
              <p>Total days: {adherence?.total_days ?? '-'}</p>
              <p>Expected doses: {adherence?.total_expected_doses ?? '-'}</p>
              <p>Taken doses: {adherence?.total_taken_doses ?? '-'}</p>
              {adherence?.message && <p>{adherence.message}</p>}
              {canOpenMedCall && adherence?.mparticipant_id && (
                <a id="case-conferencing-medcall-profile" href={`/tools/medcall2/participants/${adherence.mparticipant_id}`}>Go to MedCall Profile</a>
              )}
            </section>
          </>
        )}

        {canReadNotes && (
          <section aria-label="Recent coordinated care notes">
            <h2 className="font-semibold">Recent Coordinated Care Notes</h2>
            {notesQuery.loading && <p role="status">Loading notes...</p>}
            {notesUnavailable && (
              <div role="alert">
                <p>Unable to load coordinated care notes.</p>
                <Button onClick={() => { void notesQuery.refetch().catch(() => undefined); }}>Retry Notes</Button>
              </div>
            )}
            {!notesQuery.loading && !notesUnavailable && notes?.length === 0 && <p>No coordinated care notes recorded.</p>}
            {!notesQuery.loading && !notesUnavailable && notes?.map((note, index) => (
              <article key={index} className="border-b py-2"><p>{note.updated_at || '-'}</p><p className="whitespace-pre-wrap">{note.note || '-'}</p></article>
            ))}
            {canOpenProfile && odrparticipant && <a id="case-conferencing-participant-profile" href={`/participants/${odrparticipant}`}>Go to Participant Profile</a>}
          </section>
        )}
        {noteSaved && <p role="status">Coordinated care note saved.</p>}
        {canAddNote && odrparticipant && !noteFormOpen && (
          <Button id="case-conferencing-note-add" onClick={() => { setNoteSaved(false); setNoteFormOpen(true); }}>Add New Note</Button>
        )}
        {canAddNote && odrparticipant && noteFormOpen && (
          <CaseConferencingNoteForm
            participant={participant}
            odrparticipant={odrparticipant}
            enteredBy={String(user?.realname || user?.fullname || user?.username || '')}
            onCancel={() => setNoteFormOpen(false)}
            onSavingChange={setNoteSaving}
            onSaved={() => {
              setNoteFormOpen(false);
              setNoteSaved(true);
              if (canReadNotes) { void notesQuery.refetch().catch(() => undefined); }
            }}
          />
        )}
        <Button id="case-conferencing-details-close" variant="outline" disabled={noteSaving} onClick={onClose}>Close</Button>
      </DialogContent>
    </Dialog>
  );
};
